import { RotateCcw, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/fleet/confirm-dialog';
import { ErrorState, TableSkeleton } from '@/components/fleet/states';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useBootstrapPool, useUpdateBootstrapPool } from '@/hooks/use-bootstrap-pool';
import { ApiError } from '@/lib/api-client';
import { formatBootstrapPool, validateBootstrapPool } from '@/lib/bootstrap-validation';

/**
 * Bootstrap-edge pool editor — admin-only.
 *
 * Reads the Reality edge pool that pp-config-worker serves from Cloudflare KV
 * (`BOOTSTRAP_EDGES` → `pool:current`) and lets an operator edit it as a
 * validated JSON array, replacing the old `wrangler kv key put` workflow. Save
 * is gated behind a confirm dialog (it overwrites the live pool) and validates
 * client-side before firing; the backend re-validates authoritatively.
 */
export function BootstrapPoolPage(): JSX.Element {
  const pool = useBootstrapPool();
  const update = useUpdateBootstrapPool();

  const [draft, setDraft] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Seed the textarea once the pool loads. Keyed on the fetched payload so a
  // successful save (which invalidates + refetches) re-syncs the editor.
  useEffect(() => {
    if (pool.data) {
      setDraft(formatBootstrapPool(pool.data.edges));
      setSaveError(null);
    }
  }, [pool.data]);

  const validation = useMemo(() => validateBootstrapPool(draft), [draft]);
  const dirty = pool.data ? draft !== formatBootstrapPool(pool.data.edges) : false;
  const saving = update.isPending;

  function handleReset(): void {
    if (pool.data) {
      setDraft(formatBootstrapPool(pool.data.edges));
      setSaveError(null);
    }
  }

  function handleSaveClick(): void {
    setSaveError(null);
    if (!validation.ok) {
      setSaveError(validation.error);
      return;
    }
    setConfirmOpen(true);
  }

  function handleConfirm(): void {
    if (!validation.ok) return;
    update.mutate(validation.edges, {
      onSuccess: (result) => {
        toast.success(
          `Bootstrap pool saved — ${result.count} edge${result.count === 1 ? '' : 's'} written to KV.`,
        );
        setConfirmOpen(false);
      },
      onError: (err) => {
        setConfirmOpen(false);
        setSaveError(
          err instanceof ApiError ? err.message : 'Could not save the pool. Please try again.',
        );
      },
    });
  }

  if (pool.isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Bootstrap edges"
          description="Edit the Reality bootstrap-edge pool served from Cloudflare KV."
        />
        <TableSkeleton rows={6} />
      </div>
    );
  }

  if (pool.isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Bootstrap edges"
          description="Edit the Reality bootstrap-edge pool served from Cloudflare KV."
        />
        <ErrorState
          title="Couldn't load the bootstrap pool."
          error={pool.error}
          onRetry={() => void pool.refetch()}
        />
      </div>
    );
  }

  const edgeCount = validation.ok ? validation.edges.length : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bootstrap edges"
        description="Edit the Reality bootstrap-edge pool (KV pool:current) served by pp-config-worker."
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={!dirty || saving}
            >
              <RotateCcw className="mr-1 h-4 w-4" aria-hidden />
              Reset
            </Button>
            <Button
              size="sm"
              onClick={handleSaveClick}
              disabled={!dirty || saving || !validation.ok}
            >
              <Save className="mr-1 h-4 w-4" aria-hidden />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base">Edge pool (JSON array)</CardTitle>
          <span className="font-mono text-xs text-muted-foreground">
            {pool.data?.count ?? 0} live{edgeCount !== null ? ` · ${edgeCount} in editor` : ''}
          </span>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setSaveError(null);
            }}
            spellCheck={false}
            className="min-h-[420px] font-mono text-xs"
            aria-label="Bootstrap edge pool JSON"
            aria-invalid={!validation.ok}
            disabled={saving}
          />

          {!validation.ok && (
            <p className="text-sm text-destructive" role="alert">
              {validation.error}
            </p>
          )}

          {saveError && (
            <p className="text-sm text-destructive" role="alert">
              {saveError}
            </p>
          )}

          <p className="text-xs text-muted-foreground">
            Each edge requires <span className="font-mono">remote</span>,{' '}
            <span className="font-mono">uuid</span>, <span className="font-mono">publicKey</span> and{' '}
            <span className="font-mono">serverName</span>. Optional:{' '}
            <span className="font-mono">port</span> (443),{' '}
            <span className="font-mono">shortId</span>, <span className="font-mono">flow</span>{' '}
            (xtls-rprx-vision), <span className="font-mono">regionCountry</span>,{' '}
            <span className="font-mono">priority</span> (0). Saving replaces the entire live pool.
          </p>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(next) => (next ? setConfirmOpen(true) : setConfirmOpen(false))}
        title="Overwrite the live bootstrap pool?"
        description={
          <>
            This replaces the entire <span className="font-mono">pool:current</span> value in
            Cloudflare KV with{' '}
            <span className="font-mono">{edgeCount ?? 0}</span> edge
            {edgeCount === 1 ? '' : 's'}. Clients bootstrapping through Reality will use the new
            pool. This takes effect immediately.
          </>
        }
        confirmLabel="Overwrite pool"
        variant="destructive"
        pending={saving}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
